import { Regex } from '@shared/enums/regex';
import { Selector } from '@ngxs/store';
import { CustomerStateModel } from '../models/customer.model';
import { CustomerState } from './customer.state';
import { EquipmentType, InFlightOrder, InFlightOrderItemTypes } from '@core/models/in-flight-order.model';
import { OfferingSelectors } from '@sales/states/offering/offering.selectors';
import { VoiceSubscriptionOffering } from '../../subscription/models/offerings-response.model';
import { OfferingMbbSelectors } from '@sales/states/offering-mbb/offering-mbb.selectors';
import { OfferingMbbStateModel } from '../../../states/offering-mbb/offering-mbb.state';
import { MbbRouter, MbbSubscriptionOffering } from '../../mbb/models/mbb-offerings-response.model';
import { PrivateCustomer } from '../models/private-customer.model';
import { CustomerEquipment } from '../models/customer-equipment';
import {
  BankIdVerification,
  BankIdVerificationStatus,
  CreditApplicationApprovalProbability,
  EquipmentUpgradeAllowance,
} from '@app/core/models/initialize-response.model';
import { MobilBytteSelectors } from '../../mobilephone/states/mobilbytte/mobilbytte.selectors';
import { CustomerEquipmentUpgradeAgreement } from '../../mobilephone/models/equipment-upgrade-agreement.model';
import { CustomerErrorCode } from '@app/sales/constants/ProblemCodes';
import { isMobilBytteAgreementEligibleForBytteBytte } from '@app/core/utils';

export enum EquipmentUpgradeApprovedState {
  Approved,
  NotApproved,
  Undetermined,
}

export class CustomerSelectors {
  @Selector([CustomerState])
  public static getState(state: CustomerStateModel): CustomerStateModel {
    return state;
  }

  @Selector([CustomerState])
  static customerId(state: CustomerStateModel): string {
    return state.customerId;
  }

  @Selector([CustomerState])
  static isExistingCustomer(state: CustomerStateModel): boolean {
    return state.isExistingCustomer;
  }

  @Selector([CustomerState])
  static privateCustomer(state: CustomerStateModel): PrivateCustomer {
    return state.privateCustomer;
  }

  @Selector([CustomerState])
  static isCustomerSelected(state: CustomerStateModel): boolean {
    return !!(state.customerId || (state.privateCustomer && state.privateCustomer.fullname));
  }

  @Selector([CustomerState])
  static isCustomerSelectedFromCustomerSearch(state: CustomerStateModel): boolean {
    return state.selectedFromCustomerSearch;
  }

  @Selector([CustomerState])
  static allowanceCheckSuccessful(state: CustomerStateModel): boolean {
    return state.hasPerformedAllowanceCheck && state.authorizeResult?.subscriptionAllowance?.approved;
  }

  @Selector([CustomerState])
  static downPaymentCheckSuccessful(state: CustomerStateModel): boolean {
    return (
      state.hasPerformedDetailedCreditCheck &&
      state.authorizeResult?.downPaymentAllowance?.approved &&
      state.authorizeResult?.downPaymentAllowance?.additionalAmountAllowed > 0
    );
  }

  @Selector([CustomerState])
  static equipmentUpgradeApproval(state: CustomerStateModel): EquipmentUpgradeApprovedState {
    if (!state.hasPerformedDetailedCreditCheck) {
      return EquipmentUpgradeApprovedState.Undetermined;
    }

    let canBuyEquipmentUpgrade = false;
    if (state.authorizeResult?.approved) {
      if (
        state.authorizeResult.equipmentUpgrade?.canBuyEquipmentUpgrade &&
        state.authorizeResult.equipmentUpgrade.creditApplicationApprovalProbability ===
          CreditApplicationApprovalProbability.Probably
      ) {
        canBuyEquipmentUpgrade = true;
      }

      if (state.isExistingCustomer && state.authorizeResult.downPaymentAllowance?.reason) {
        canBuyEquipmentUpgrade = false;
      }
    }

    return canBuyEquipmentUpgrade ? EquipmentUpgradeApprovedState.Approved : EquipmentUpgradeApprovedState.NotApproved;
  }

  @Selector([CustomerState])
  static mobilBytteApproval(state: CustomerStateModel): EquipmentUpgradeApprovedState {
    if (state.hasPerformedAllowanceCheck && !state.authorizeResult.approved) {
      return EquipmentUpgradeApprovedState.NotApproved;
    }

    if (!state.hasPerformedDetailedCreditCheck) {
      return EquipmentUpgradeApprovedState.Undetermined;
    }

    const canBuyEquipmentUpgrade =
      state.authorizeResult?.approved &&
      state.authorizeResult.equipmentUpgrade?.availableCredit > 0 &&
      state.authorizeResult.equipmentUpgrade?.canBuyEquipmentUpgrade;

    return canBuyEquipmentUpgrade ? EquipmentUpgradeApprovedState.Approved : EquipmentUpgradeApprovedState.NotApproved;
  }

  @Selector([CustomerState])
  static hasSubscriptionsAvailableForRedemption(state: CustomerStateModel): boolean {
    return state.authorizeResult?.equipmentUpgrade?.hasSubAvailableForRedemption;
  }

  @Selector([CustomerState])
  static isEquipmentUpgradePreCheckSuccessful(state: CustomerStateModel): boolean {
    return state.hasPerformedDetailedCreditCheck && state.authorizeResult?.equipmentUpgrade?.canBuyEquipmentUpgrade;
  }

  @Selector([CustomerState])
  static numberOfSubscriptionAllowed(state: CustomerStateModel): number {
    if (state.isExistingCustomer || state.hasPerformedDetailedCreditCheck) {
      return state.authorizeResult?.subscriptionAllowance?.totalAllowedAmount;
    }
    return 5;
  }

  @Selector([CustomerState])
  static isValidCustomer(customer: CustomerStateModel): boolean {
    return (
      (customer.isExistingCustomer && Boolean(customer.customerId)) ||
      (!customer.isExistingCustomer && customer.privateCustomer?.fullname && customer.hasPerformedAllowanceCheck)
    );
  }

  @Selector([CustomerState])
  static isValidPrivateCustomerDetails(customerState: CustomerStateModel): boolean {
    return (
      !!customerState.privateCustomer &&
      !!customerState.privateCustomer.address &&
      !!customerState.privateCustomer.postCode &&
      !!customerState.privateCustomer.city
    );
  }

  @Selector([CustomerState])
  static isPrivateCustomerPhoneNumberValid(customer: CustomerStateModel): boolean {
    return !!customer.privateCustomer.phone && RegExp(Regex.Phone).test(customer.privateCustomer.phone);
  }

  @Selector([CustomerState])
  static isPrivateCustomerEmailValid(customer: CustomerStateModel): boolean {
    return !!customer.privateCustomer.email && RegExp(Regex.Email).test(customer.privateCustomer.email);
  }

  @Selector([CustomerState])
  static isLoadingProducts(customer: CustomerStateModel): boolean {
    return customer.isLoadingProducts;
  }

  @Selector([CustomerState])
  static isLoadingInFlightOrders(customer: CustomerStateModel): boolean {
    return customer.isLoadingInFlightOrders;
  }

  @Selector([CustomerState])
  static inFlightOrders(customer: CustomerStateModel): InFlightOrder[] {
    return customer.inFlightOrders;
  }

  @Selector([CustomerSelectors.inFlightOrders, OfferingSelectors.getAllOfferings])
  static inFlightVoiceOrders(inFlightOrders: InFlightOrder[], offerings: VoiceSubscriptionOffering[]): InFlightOrder[] {
    if (!offerings) {
      return [];
    }

    return inFlightOrders.filter((inFlightOrder: InFlightOrder) =>
      offerings.some((offering: VoiceSubscriptionOffering) => {
        return offering.id === inFlightOrder.offeringId;
      })
    );
  }

  @Selector([CustomerSelectors.inFlightOrders, OfferingMbbSelectors.getState])
  static inFlightMbbOrders(inFlightOrders: InFlightOrder[], offeringMbbState: OfferingMbbStateModel): InFlightOrder[] {
    if (!offeringMbbState) {
      return [];
    }

    return inFlightOrders.filter((inFlightOrder: InFlightOrder) => {
      const hasConnectedMbbOffering = offeringMbbState.products.some((mbbOffering: MbbSubscriptionOffering) => {
        return mbbOffering.id === inFlightOrder.offeringId;
      });

      const hasConnectedMbbRouter = offeringMbbState.routers.some((mbbRouter: MbbRouter) =>
        mbbRouter.bundles.some(bundle => bundle.offeringId === inFlightOrder.offeringId)
      );

      return hasConnectedMbbOffering || hasConnectedMbbRouter;
    });
  }

  @Selector([CustomerSelectors.inFlightOrders, OfferingMbbSelectors.getState])
  static inFlightMobilePhoneOrders(
    inFlightOrders: InFlightOrder[],
    offeringMbbState: OfferingMbbStateModel
  ): InFlightOrder[] {
    if (!offeringMbbState) {
      return [];
    }

    return inFlightOrders.filter((inFlightOrder: InFlightOrder) => {
      const hasConnectedMbbRouter = offeringMbbState.routers.some((mbbRouter: MbbRouter) =>
        mbbRouter.bundles.some(bundle => bundle.offeringId === inFlightOrder.offeringId)
      );

      return (
        inFlightOrder.orderItemType === InFlightOrderItemTypes.AddEquipment &&
        inFlightOrder.equipmentType === EquipmentType.Handset &&
        !hasConnectedMbbRouter
      );
    });
  }

  @Selector([CustomerSelectors.inFlightOrders])
  static inFlightAccessoryOrders(inFlightOrders: InFlightOrder[]): InFlightOrder[] {
    return inFlightOrders.filter(
      (inFlightOrder: InFlightOrder) =>
        inFlightOrder.orderItemType === InFlightOrderItemTypes.AddEquipment &&
        inFlightOrder.equipmentType === EquipmentType.Accessory
    );
  }

  @Selector([CustomerSelectors.inFlightOrders])
  static inFlightServicesOrders(inFlightOrders: InFlightOrder[]): InFlightOrder[] {
    return inFlightOrders.filter(
      (inFlightOrder: InFlightOrder) =>
        inFlightOrder.orderItemType === InFlightOrderItemTypes.AddSlaveSim ||
        inFlightOrder.orderItemType === InFlightOrderItemTypes.AddService
    );
  }

  @Selector([CustomerState])
  static customerDetails(state: CustomerStateModel): PrivateCustomer {
    return state.privateCustomer;
  }

  @Selector([CustomerState])
  static customerBirthdate(state: CustomerStateModel): Date {
    return state.privateCustomer?.birthdate;
  }

  @Selector([CustomerState])
  static isMbbAllowanceCheckSuccessful(state: CustomerStateModel): boolean {
    return state.hasPerformedAllowanceCheck && state.authorizeResult?.mbbSubscriptionAllowance?.approved;
  }

  @Selector([CustomerState])
  static hasPerformedAllowanceCheck(state: CustomerStateModel): boolean {
    return state.hasPerformedAllowanceCheck;
  }

  @Selector([CustomerState])
  static hasPerformedDetailedCreditCheck(state: CustomerStateModel): boolean {
    return state.hasPerformedDetailedCreditCheck;
  }

  @Selector([CustomerState])
  static isCustomerAllowedToAddAdditionalSubscriptions(state: CustomerStateModel): boolean {
    return (
      state.isExistingCustomer &&
      state.authorizeResult?.subscriptionAllowance &&
      state.authorizeResult?.subscriptionAllowance?.additionalAmountAllowed === 0
    );
  }

  @Selector([CustomerState])
  static customerEquipment(state: CustomerStateModel): CustomerEquipment[] {
    return state.customerEquipment;
  }

  @Selector([CustomerState])
  static initialCustomerInfo(state: CustomerStateModel): PrivateCustomer {
    return state.initialCustomerInfo;
  }

  @Selector([CustomerState])
  static hasCustomerInfoChanged(state: CustomerStateModel): boolean {
    return (
      state.isExistingCustomer &&
      (state.initialCustomerInfo?.email !== state.privateCustomer?.email ||
        state.initialCustomerInfo?.phone !== state.privateCustomer?.phone)
    );
  }

  @Selector([CustomerState])
  static isCustomerAllowedToAddTradeIn(state: CustomerStateModel): boolean {
    return state.authorizeResult?.tradeIn?.canUseTradeIn;
  }

  @Selector([CustomerState])
  static numberOfTradeInsAvailableForUse(state: CustomerStateModel): number {
    return state.authorizeResult?.tradeIn?.numberOfTradeInsAvailableForUse;
  }

  @Selector([CustomerState])
  static hasCreditCheckError(state: CustomerStateModel): boolean {
    const isValidCustomer = CustomerSelectors.isValidCustomer(state);
    const errorInAllowanceCheck =
      (state.hasPerformedAllowanceCheck && !isValidCustomer) ||
      (state.hasPerformedAllowanceCheck &&
        isValidCustomer &&
        state.authorizeResult.subscriptionAllowance &&
        !state.authorizeResult.subscriptionAllowance.approved);

    const creditFreezeError = state.isExistingCustomer && state.authorizeResult.downPaymentAllowance?.reason;

    return !!state.customerError || errorInAllowanceCheck || !!creditFreezeError;
  }

  @Selector([CustomerState])
  static equipmentUpgradeAllowance(state: CustomerStateModel): EquipmentUpgradeAllowance {
    return state.authorizeResult.equipmentUpgrade;
  }

  @Selector([
    CustomerSelectors.hasPerformedAllowanceCheck,
    CustomerSelectors.hasPerformedDetailedCreditCheck,
    CustomerSelectors.equipmentUpgradeAllowance,
    MobilBytteSelectors.existingEquipmentUpgradeAgreements,
  ])
  static canPerformMobilBytteBytte(
    hasPerformedAllowanceCheck: boolean,
    hasPerformedDetailedCreditCheck: boolean,
    equipmentUpgradeAllowance: EquipmentUpgradeAllowance,
    existingEquipmentUpgradeAgreements: CustomerEquipmentUpgradeAgreement[]
  ): boolean {
    if (!hasPerformedAllowanceCheck || !hasPerformedDetailedCreditCheck) {
      return false;
    }

    const hasMobilBytteAgreementsEligibleForUpgrade = existingEquipmentUpgradeAgreements.some(
      (existingAgreement: CustomerEquipmentUpgradeAgreement) => isMobilBytteAgreementEligibleForBytteBytte(existingAgreement)
    );

    return equipmentUpgradeAllowance.redemptionCreditDecision && hasMobilBytteAgreementsEligibleForUpgrade;
  }

  @Selector([
    CustomerSelectors.hasPerformedAllowanceCheck,
    CustomerSelectors.hasPerformedDetailedCreditCheck,
    CustomerSelectors.equipmentUpgradeAllowance,
    MobilBytteSelectors.existingEquipmentUpgradeAgreements,
  ])
  static isMobilBytteBytteDeniedByCreditDecision(
    hasPerformedAllowanceCheck: boolean,
    hasPerformedDetailedCreditCheck: boolean,
    equipmentUpgradeAllowance: EquipmentUpgradeAllowance,
    existingEquipmentUpgradeAgreements: CustomerEquipmentUpgradeAgreement[]
  ): boolean {
    if (!hasPerformedAllowanceCheck || !hasPerformedDetailedCreditCheck) {
      return false;
    }

    const hasMobilBytteAgreementsEligibleForUpgrade = existingEquipmentUpgradeAgreements.some(
      (existingAgreement: CustomerEquipmentUpgradeAgreement) =>
        existingAgreement.canBeUpgraded && existingAgreement.remainingNumberOfPeriodsUntilUpgrade <= 0
    );

    return !equipmentUpgradeAllowance.redemptionCreditDecision && hasMobilBytteAgreementsEligibleForUpgrade;
  }

  @Selector([CustomerState])
  static customerErrorCode(state: CustomerStateModel): CustomerErrorCode | null {
    return state.customerErrorCode;
  }

  @Selector([CustomerState])
  static isMissingContactInfo(state: CustomerStateModel): boolean {
    return !state.privateCustomer?.email || !state.privateCustomer?.phone;
  }

  @Selector([CustomerState])
  static bankIdVerification(state: CustomerStateModel): BankIdVerification {
    return state.bankIdVerification;
  }

  @Selector([CustomerState])
  static isCustomerVerifiedWithBankId(state: CustomerStateModel): boolean {
    return (
      state.bankIdVerification?.status === BankIdVerificationStatus.Authenticated ||
      state.bankIdVerification?.status === BankIdVerificationStatus.SsnIsSameAsCustomerSsn
    );
  }

  @Selector([CustomerState])
  static isAuthenticatedFromManny(state: CustomerStateModel): boolean {
    return state.bankIdVerification?.isAuthenticatedFromManny || false;
  }

  @Selector([CustomerState])
  static isUnsuccessfullyAuthenticatedFromManny(state: CustomerStateModel): boolean {
    return state.bankIdVerification?.isAuthenticatedFromManny === false;
  }
}
